import * as Sentry from '@sentry/react';

const STORAGE_KEY = 'sentryConfig';

export interface SentryConfigOptions {
    enabled: boolean;
    tracesSampleRate: number;
    replaysSessionSampleRate: number;
    replaysOnErrorSampleRate: number;
    logLevels: string[];
    tracingEnabled: boolean;
    replayEnabled: boolean;
}

const DEFAULT_CONFIG: SentryConfigOptions = {
    enabled: true,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.5,
    replaysOnErrorSampleRate: 0.5,
    logLevels: ['error'],
    tracingEnabled: true,
    replayEnabled: true,
};

class SentryConfigService {
    private static instance: SentryConfigService;
    private config: SentryConfigOptions = { ...DEFAULT_CONFIG };

    static getInstance(): SentryConfigService {
        if (!SentryConfigService.instance) {
            SentryConfigService.instance = new SentryConfigService();
            SentryConfigService.instance.loadFromStorage();
        }
        return SentryConfigService.instance;
    }

    private loadFromStorage(): void {
        if (typeof window === 'undefined') return;
        try {
            const stored = window.localStorage.getItem(STORAGE_KEY);
            if (stored) {
                this.config = { ...DEFAULT_CONFIG, ...JSON.parse(stored) };
            }
        } catch {
            this.config = { ...DEFAULT_CONFIG };
        }
    }

    private saveToStorage(): void {
        if (typeof window === 'undefined') return;
        try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(this.config));
        } catch {}
    }

    getConfig(): SentryConfigOptions {
        return { ...this.config };
    }

    updateConfig(updates: Partial<SentryConfigOptions>): void {
        this.config = { ...this.config, ...updates };
        this.saveToStorage();
    }

    resetConfig(): void {
        this.config = { ...DEFAULT_CONFIG };
        this.saveToStorage();
    }

    isEnabled(): boolean {
        return this.config.enabled;
    }

    isTracingEnabled(): boolean {
        return this.config.enabled && this.config.tracingEnabled;
    }

    getTracesSampleRate(): number {
        if (!this.config.enabled || !this.config.tracingEnabled) return 0;
        return this.config.tracesSampleRate;
    }

    isReplayEnabled(): boolean {
        return this.config.enabled && this.config.replayEnabled;
    }

    shouldCaptureLogLevel(level: string): boolean {
        return this.config.enabled && this.config.logLevels.includes(level);
    }

    setUserContext(context: {
        userId?: string;
        username?: string;
        email?: string;
        tenantId?: string;
        employeeId?: string;
        companyName?: string;
    }): void {
        Sentry.setUser({
            id: context.userId,
            username: context.username,
            email: context.email,
            tenantId: context.tenantId,
            employeeId: context.employeeId,
            companyName: context.companyName,
        } as any);
    }

    clearUserContext(): void {
        Sentry.setUser(null);
    }

    }

const sentryConfigService = SentryConfigService.getInstance();


export const SentryDynamicConfig = {
    tracesSampler: (_samplingContext: any): number => {
        return sentryConfigService.getTracesSampleRate();
    },
    beforeSend: (event: any, _hint: any): any | null => {
        if (!sentryConfigService.isEnabled()) return null;
        return event;
    },
    beforeSendTransaction: (event: any): any | null => {
        if (!sentryConfigService.isTracingEnabled()) return null;
        return event;
    },
};

export default sentryConfigService;
